"use client";

import { addDays, addMonths, addWeeks, endOfWeek, format, isValid, parse } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { dayMarker } from "@/lib/clinic-clock";
interface DateSuggestion {
  id: string;
  label: string;
  date: Date;
  isCustom?: boolean;
}
type DateDirection = "future" | "past" | "both";
interface UseSmartDateInputOptions {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  customDefaultSuggestions?: DateSuggestion[];
  includeCustomOption?: boolean;
  onSuggestionSelect?: (suggestion: DateSuggestion) => void;
  clearInputAfterSelection?: boolean; // JumpToDate clears, SmartDateInput shows formatted date
  direction?: DateDirection; // "future" (default) or "past" for suggestions
  /**
   * When set, the input text is synced from this string instead of formatting `value` as a single
   * date. Used by SmartDateInput in range mode where `value` is only the range start.
   */
  controlledInputDisplay?: string;
  /** When set, used after parsing typed input instead of a single-date format string. */
  formatParsedDateDisplay?: (date: Date) => string;
}

// Month names and abbreviations for parsing
const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const monthAbbreviations = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Matches inputs that name a month and day with no year ("june 15",
// "15 jun", "decem 5", "7/15"). Used to decide when to offer an alternate-year
// suggestion under direction="both". Intentionally excludes offset phrasing
// like "1 week" or "in 1 month" — those don't disambiguate by year.
const yearlessMonthDayPattern = /^(?:\d{1,2}\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*|(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+\d{1,2}|\d{1,2}\/\d{1,2})$/i;
export function useSmartDateInput({
  value,
  onChange,
  customDefaultSuggestions,
  includeCustomOption = false,
  onSuggestionSelect,
  clearInputAfterSelection = true,
  direction = "future",
  controlledInputDisplay,
  formatParsedDateDisplay
}: UseSmartDateInputOptions) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState<DateSuggestion[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const justOpenedRef = useRef(false);
  // The clinic's today on the enclosing scope's clock, so "today" in a form
  // about one location is that location's day.
  const clock = useClinicClock();
  const todayMarker = () => dayMarker(clock.today());

  // Parse natural language input to date
  const parseNaturalLanguage = (input: string): Date | null => {
    if (!input || typeof input !== "string") return null;
    const cleanInput = input.toLowerCase().trim();
    if (!cleanInput) return null;

    // Get today at start of day to avoid time issues
    const today = todayMarker();

    // Handle "today", "tomorrow"
    if (cleanInput === "today") return new Date(today);
    if (cleanInput === "tomorrow") return addDays(today, 1);

    // Handle "yesterday" (edge case)
    if (cleanInput === "yesterday") return addDays(today, -1);

    // Handle "in X days/weeks/months/years"
    const inPattern = /^in\s+(\d+)\s+(day|days|week|weeks|month|months|year|years)$/;
    const inMatch = cleanInput.match(inPattern);
    if (inMatch) {
      const amount = parseInt(inMatch[1], 10);
      const unit = inMatch[2];

      // Validate amount is reasonable
      if (isNaN(amount) || amount < 0 || amount > 999) return null;
      if (unit.startsWith("day")) return addDays(today, amount);
      if (unit.startsWith("week")) return addWeeks(today, amount);
      if (unit.startsWith("month")) return addMonths(today, amount);
      if (unit.startsWith("year")) return addMonths(today, amount * 12);
    }

    // Handle "X days/weeks/months/years" (without "in")
    const directPattern = /^(\d+)\s+(day|days|week|weeks|month|months|year|years)$/;
    const directMatch = cleanInput.match(directPattern);
    if (directMatch) {
      const amount_0 = parseInt(directMatch[1], 10);
      const unit_0 = directMatch[2];

      // Validate amount is reasonable
      if (isNaN(amount_0) || amount_0 < 0 || amount_0 > 999) return null;
      if (unit_0.startsWith("day")) return addDays(today, amount_0);
      if (unit_0.startsWith("week")) return addWeeks(today, amount_0);
      if (unit_0.startsWith("month")) return addMonths(today, amount_0);
      if (unit_0.startsWith("year")) return addMonths(today, amount_0 * 12);
    }

    // Handle "X years from now/today" variations
    const yearsFromPattern = /^(\d+)\s+years?\s+from\s+(now|today)$/;
    const yearsFromMatch = cleanInput.match(yearsFromPattern);
    if (yearsFromMatch) {
      const amount_1 = parseInt(yearsFromMatch[1], 10);
      if (!isNaN(amount_1) && amount_1 >= 0 && amount_1 <= 999) {
        return addMonths(today, amount_1 * 12);
      }
    }

    // Handle "X days/weeks/months/years ago"
    const agoPattern = /^(\d+)\s+(day|days|week|weeks|month|months|year|years)\s+ago$/;
    const agoMatch = cleanInput.match(agoPattern);
    if (agoMatch) {
      const amount_2 = parseInt(agoMatch[1], 10);
      const unit_1 = agoMatch[2];

      // Validate amount is reasonable
      if (isNaN(amount_2) || amount_2 < 0 || amount_2 > 999) return null;
      if (unit_1.startsWith("day")) return addDays(today, -amount_2);
      if (unit_1.startsWith("week")) return addWeeks(today, -amount_2);
      if (unit_1.startsWith("month")) return addMonths(today, -amount_2);
      if (unit_1.startsWith("year")) return addMonths(today, -amount_2 * 12);
    }

    // Handle "last week/month/year"
    if (cleanInput === "last week") return addWeeks(today, -1);
    if (cleanInput === "last month") return addMonths(today, -1);
    if (cleanInput === "last year") return addMonths(today, -12);

    // Handle "end of week/month"
    if (cleanInput === "end of week" || cleanInput === "end of the week") {
      return endOfWeek(today, {
        weekStartsOn: 0
      });
    }
    if (cleanInput === "end of month" || cleanInput === "end of the month") {
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return endOfMonth;
    }

    // Handle "next week/month/year"
    if (cleanInput === "next week") return addWeeks(today, 1);
    if (cleanInput === "next month") return addMonths(today, 1);
    if (cleanInput === "next year") return addMonths(today, 12);

    // Handle date formats with explicit year like "Sep 28, 2025", "28 mar 2025", "march 28, 2025"
    // Try "MMM DD, YYYY" format (Sep 28, 2025)
    const monthDayYearCommaPattern = /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{1,2}),?\s+(\d{4})$/;
    const monthDayYearCommaMatch = cleanInput.match(monthDayYearCommaPattern);
    if (monthDayYearCommaMatch) {
      const month = monthDayYearCommaMatch[1];
      const day = parseInt(monthDayYearCommaMatch[2], 10);
      const year = parseInt(monthDayYearCommaMatch[3], 10);

      // Validate day and year
      if (day < 1 || day > 31 || year < 1900 || year > 2100) return null;
      try {
        // Try full month name first
        let parsed = parse(`${month} ${day} ${year}`, "MMMM d yyyy", today);
        if (isValid(parsed)) return parsed;

        // Try short month format
        parsed = parse(`${month} ${day} ${year}`, "MMM d yyyy", today);
        if (isValid(parsed)) return parsed;
      } catch {
        // Continue to next pattern
      }
    }

    // Try "DD MMM YYYY" format (28 mar 2025)
    const dayMonthYearPattern = /^(\d{1,2})\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{4})$/;
    const dayMonthYearMatch = cleanInput.match(dayMonthYearPattern);
    if (dayMonthYearMatch) {
      const day_0 = parseInt(dayMonthYearMatch[1], 10);
      const month_0 = dayMonthYearMatch[2];
      const year_0 = parseInt(dayMonthYearMatch[3], 10);

      // Validate day and year
      if (day_0 < 1 || day_0 > 31 || year_0 < 1900 || year_0 > 2100) return null;
      try {
        // Try full month name first
        let parsed_0 = parse(`${day_0} ${month_0} ${year_0}`, "d MMMM yyyy", today);
        if (isValid(parsed_0)) return parsed_0;

        // Try short month format
        parsed_0 = parse(`${day_0} ${month_0} ${year_0}`, "d MMM yyyy", today);
        if (isValid(parsed_0)) return parsed_0;
      } catch {
        // Continue to next pattern
      }
    }

    // Handle date formats like "28 mar", "march 28", "mar 28" (current year)
    const currentYear = today.getFullYear();

    // Try "DD MMM" format (28 mar, 28 march)
    const dayMonthPattern = /^(\d{1,2})\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|may|june|july|august|september|october|november|december)$/;
    const dayMonthMatch = cleanInput.match(dayMonthPattern);
    if (dayMonthMatch) {
      const day_1 = parseInt(dayMonthMatch[1], 10);
      const month_1 = dayMonthMatch[2];

      // Validate day
      if (day_1 < 1 || day_1 > 31) return null;
      try {
        // Try full month name first
        let parsed_1 = parse(`${day_1} ${month_1} ${currentYear}`, "d MMMM yyyy", today);
        if (isValid(parsed_1)) {
          // If the parsed date is in the past, try next year
          if (parsed_1 < today) {
            const nextYear = parse(`${day_1} ${month_1} ${currentYear + 1}`, "d MMMM yyyy", today);
            if (isValid(nextYear)) return nextYear;
          }
          return parsed_1;
        }

        // Try short month format
        parsed_1 = parse(`${day_1} ${month_1} ${currentYear}`, "d MMM yyyy", today);
        if (isValid(parsed_1)) {
          // If the parsed date is in the past, try next year
          if (parsed_1 < today) {
            const nextYear_0 = parse(`${day_1} ${month_1} ${currentYear + 1}`, "d MMM yyyy", today);
            if (isValid(nextYear_0)) return nextYear_0;
          }
          return parsed_1;
        }
      } catch {
        // Continue to next pattern
      }
    }

    // Try "MMM DD" format (mar 28, march 28)
    const monthDayPattern = /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{1,2})$/;
    const monthDayMatch = cleanInput.match(monthDayPattern);
    if (monthDayMatch) {
      const month_2 = monthDayMatch[1];
      const day_2 = parseInt(monthDayMatch[2], 10);

      // Validate day
      if (day_2 < 1 || day_2 > 31) return null;
      try {
        // Try full month name first
        let parsed_2 = parse(`${month_2} ${day_2} ${currentYear}`, "MMMM d yyyy", today);
        if (isValid(parsed_2)) {
          // If the parsed date is in the past, try next year
          if (parsed_2 < today) {
            const nextYear_1 = parse(`${month_2} ${day_2} ${currentYear + 1}`, "MMMM d yyyy", today);
            if (isValid(nextYear_1)) return nextYear_1;
          }
          return parsed_2;
        }

        // Try short month format
        parsed_2 = parse(`${month_2} ${day_2} ${currentYear}`, "MMM d yyyy", today);
        if (isValid(parsed_2)) {
          // If the parsed date is in the past, try next year
          if (parsed_2 < today) {
            const nextYear_2 = parse(`${month_2} ${day_2} ${currentYear + 1}`, "MMM d yyyy", today);
            if (isValid(nextYear_2)) return nextYear_2;
          }
          return parsed_2;
        }
      } catch {
        // Continue
      }
    }

    // Handle common date formats: MM/DD, MM/DD/YY, MM/DD/YYYY
    const slashPattern = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2}|\d{4}))?$/;
    const slashMatch = cleanInput.match(slashPattern);
    if (slashMatch) {
      const month_3 = parseInt(slashMatch[1], 10);
      const day_3 = parseInt(slashMatch[2], 10);
      const hasExplicitYear = !!slashMatch[3];
      let year_1 = hasExplicitYear ? parseInt(slashMatch[3], 10) : currentYear;

      // Handle 2-digit years
      if (year_1 < 100) {
        year_1 += year_1 < 50 ? 2000 : 1900;
      }

      // Validate month and day
      if (month_3 < 1 || month_3 > 12 || day_3 < 1 || day_3 > 31) return null;
      try {
        const parsed_3 = new Date(year_1, month_3 - 1, day_3);
        if (isValid(parsed_3) && parsed_3.getMonth() === month_3 - 1 && parsed_3.getDate() === day_3) {
          // Yearless slash inputs jump forward to the next occurrence when
          // already past, matching the word-input behavior above. Skipped
          // for direction="past" so historical-date fields (e.g. measurement
          // dialogs) keep the current-year past date.
          if (!hasExplicitYear && parsed_3 < today && direction !== "past") {
            const nextYear_3 = new Date(year_1 + 1, month_3 - 1, day_3);
            if (isValid(nextYear_3) && nextYear_3.getMonth() === month_3 - 1 && nextYear_3.getDate() === day_3) {
              return nextYear_3;
            }
          }
          return parsed_3;
        }
      } catch {
        // Invalid date
      }
    }

    // Handle ISO format: YYYY-MM-DD
    const isoPattern = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
    const isoMatch = cleanInput.match(isoPattern);
    if (isoMatch) {
      const year_2 = parseInt(isoMatch[1], 10);
      const month_4 = parseInt(isoMatch[2], 10);
      const day_4 = parseInt(isoMatch[3], 10);

      // Validate ranges
      if (year_2 < 1900 || year_2 > 2100 || month_4 < 1 || month_4 > 12 || day_4 < 1 || day_4 > 31) return null;
      try {
        const parsed_4 = new Date(year_2, month_4 - 1, day_4);
        if (isValid(parsed_4) && parsed_4.getMonth() === month_4 - 1 && parsed_4.getDate() === day_4) {
          return parsed_4;
        }
      } catch {
        // Invalid date
      }
    }

    // Handle "DD partial_month" format (e.g., "12 decem", "5 jan")
    const dayPartialMonthPattern = /^(\d{1,2})\s+([a-z]{3,})$/;
    const dayPartialMonthMatch = cleanInput.match(dayPartialMonthPattern);
    if (dayPartialMonthMatch) {
      const dayNum = parseInt(dayPartialMonthMatch[1], 10);
      const partialMonth = dayPartialMonthMatch[2];
      if (dayNum >= 1 && dayNum <= 31) {
        // Find matching month
        const monthIndex = monthNames.findIndex(month_5 => month_5.toLowerCase().startsWith(partialMonth) || monthAbbreviations[monthNames.indexOf(month_5)].toLowerCase().startsWith(partialMonth));
        if (monthIndex !== -1) {
          const currentYear_0 = todayMarker().getFullYear();
          const targetDate = new Date(currentYear_0, monthIndex, dayNum);

          // If the day is valid for the target month
          if (targetDate.getDate() === dayNum) {
            // If the date is in the past, try next year
            if (targetDate < today) {
              const nextYear_4 = new Date(currentYear_0 + 1, monthIndex, dayNum);
              if (nextYear_4.getDate() === dayNum) {
                return nextYear_4;
              }
            }
            return targetDate;
          }
        }
      }
    }

    // Handle single numbers (1-31) as day of current month
    const numberPattern = /^(\d{1,2})$/;
    const numberMatch = cleanInput.match(numberPattern);
    if (numberMatch) {
      const dayNum_0 = parseInt(numberMatch[1], 10);
      if (dayNum_0 >= 1 && dayNum_0 <= 31) {
        const currentDate = todayMarker();
        const targetDate_0 = new Date(currentDate.getFullYear(), currentDate.getMonth(), dayNum_0);

        // If the day is valid for the current month, return it
        if (targetDate_0.getDate() === dayNum_0) {
          // If the date is in the past, move to next month
          if (targetDate_0 < today) {
            const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, dayNum_0);
            // Check if the day is valid for next month
            if (nextMonth.getDate() === dayNum_0) {
              return nextMonth;
            }
          }
          return targetDate_0;
        }
      }
    }
    return null;
  };

  // Helper function to get the right-side display text for suggestions
  const getSuggestionRightText = (suggestion: DateSuggestion): string => {
    // Always show the formatted date that the user is selecting
    return format(suggestion.date, "MMM d, yyyy");
  };

  // Generate the standard suggestions (used for pattern matching when typing)
  const generateStandardSuggestions = (): DateSuggestion[] => {
    const today_0 = todayMarker();
    let suggestions_0: DateSuggestion[];
    if (direction === "past") {
      suggestions_0 = [{
        id: "today",
        label: "Today",
        date: new Date(today_0)
      }, {
        id: "yesterday",
        label: "Yesterday",
        date: addDays(today_0, -1)
      }, {
        id: "1-week-ago",
        label: "1 week ago",
        date: addWeeks(today_0, -1)
      }, {
        id: "2-weeks-ago",
        label: "2 weeks ago",
        date: addWeeks(today_0, -2)
      }, {
        id: "1-month-ago",
        label: "1 month ago",
        date: addMonths(today_0, -1)
      }, {
        id: "3-months-ago",
        label: "3 months ago",
        date: addMonths(today_0, -3)
      }];
    } else if (direction === "both") {
      // Single-word past/future labels only — multi-word past entries like
      // "1 week ago" overlap with future inputs ("1 week") under the
      // current substring word-filter and pull in the wrong direction.
      suggestions_0 = [{
        id: "today",
        label: "Today",
        date: new Date(today_0)
      }, {
        id: "tomorrow",
        label: "Tomorrow",
        date: addDays(today_0, 1)
      }, {
        id: "next-week",
        label: "In 1 week",
        date: addWeeks(today_0, 1)
      }, {
        id: "yesterday",
        label: "Yesterday",
        date: addDays(today_0, -1)
      }];
    } else {
      suggestions_0 = [{
        id: "today",
        label: "Today",
        date: new Date(today_0)
      }, {
        id: "tomorrow",
        label: "Tomorrow",
        date: addDays(today_0, 1)
      }, {
        id: "next-week",
        label: "In 1 week",
        date: addWeeks(today_0, 1)
      }, {
        id: "next-month",
        label: "In 1 month",
        date: addMonths(today_0, 1)
      }];
    }
    if (includeCustomOption) {
      suggestions_0.unshift({
        id: "custom",
        label: "Custom date...",
        date: todayMarker(),
        isCustom: true
      } as DateSuggestion);
    }
    return suggestions_0;
  };

  // Get the suggestions to show when input is empty
  const getEmptyInputSuggestions = (): DateSuggestion[] => {
    // If custom default suggestions are provided, use them for empty input
    if (customDefaultSuggestions && customDefaultSuggestions.length > 0) {
      const suggestions_1 = [...customDefaultSuggestions];

      // Add "Custom date..." option at the beginning if requested
      if (includeCustomOption) {
        suggestions_1.unshift({
          id: "custom",
          label: "Custom date...",
          date: todayMarker(),
          isCustom: true
        } as DateSuggestion);
      }
      return suggestions_1;
    }
    return generateStandardSuggestions();
  };

  // Filter and generate suggestions based on input
  const generateSuggestions = (input_0: string): DateSuggestion[] => {
    const cleanInput_0 = input_0.trim().toLowerCase();

    // If no input, show custom or default suggestions
    if (!cleanInput_0) {
      return getEmptyInputSuggestions();
    }

    // User is typing - use standard suggestions for matching (not custom ones)
    const predefined = generateStandardSuggestions();
    const suggestions_2: DateSuggestion[] = [];

    // Check for month name patterns (nov, november, etc.)
    const monthPattern = /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|may|june|july|august|september|october|november|december)$/i;
    const monthMatch = cleanInput_0.match(monthPattern);
    if (monthMatch) {
      const monthInput = monthMatch[1].toLowerCase();
      const today_1 = todayMarker();
      const currentDay = today_1.getDate();
      const currentYear_1 = today_1.getFullYear();

      // Find matching month
      const monthIndex_0 = monthNames.findIndex(month_6 => month_6.toLowerCase().startsWith(monthInput) || monthAbbreviations[monthNames.indexOf(month_6)].toLowerCase().startsWith(monthInput));
      if (monthIndex_0 !== -1) {
        // Create date for same day in the matched month
        let targetDate_1 = new Date(currentYear_1, monthIndex_0, currentDay);

        // If the date is in the past, use next year
        if (targetDate_1 < today_1) {
          targetDate_1 = new Date(currentYear_1 + 1, monthIndex_0, currentDay);
        }

        // Handle case where the day doesn't exist in the target month (e.g., Feb 30)
        if (targetDate_1.getMonth() !== monthIndex_0) {
          // Use the last day of the target month
          targetDate_1 = new Date(targetDate_1.getFullYear(), monthIndex_0 + 1, 0);
        }
        suggestions_2.push({
          id: `month-${monthIndex_0}`,
          label: format(targetDate_1, "MMM d, yyyy"),
          date: targetDate_1,
          isCustom: true
        });
      }
    }

    // Check for day of week patterns (friday, monday, etc.) and "next friday"
    const dayOfWeekPattern = /^(next\s+)?(m|mo|mon|mond|monda|monday|t|tu|tue|tues|tuesd|tuesda|tuesday|w|we|wed|wedn|wedne|wednes|wednesd|wednesda|wednesday|th|thu|thur|thurs|thursd|thursda|thursday|f|fr|fri|frid|frida|friday|s|sa|sat|satu|satur|saturd|saturda|saturday|su|sun|sund|sunda|sunday)$/i;
    const dayOfWeekMatch = cleanInput_0.match(dayOfWeekPattern);
    if (dayOfWeekMatch) {
      const hasNext = !!dayOfWeekMatch[1]; // Check if "next" is present
      const dayInput = dayOfWeekMatch[2].toLowerCase();
      const today_2 = todayMarker();

      // Map day names to day numbers (0 = Sunday, 1 = Monday, etc.) and full names
      const dayMap: {
        [key: string]: {
          num: number;
          fullName: string;
        };
      } = {
        // Sunday variations
        su: {
          num: 0,
          fullName: "Sunday"
        },
        sun: {
          num: 0,
          fullName: "Sunday"
        },
        sund: {
          num: 0,
          fullName: "Sunday"
        },
        sunda: {
          num: 0,
          fullName: "Sunday"
        },
        sunday: {
          num: 0,
          fullName: "Sunday"
        },
        // Monday variations
        m: {
          num: 1,
          fullName: "Monday"
        },
        mo: {
          num: 1,
          fullName: "Monday"
        },
        mon: {
          num: 1,
          fullName: "Monday"
        },
        mond: {
          num: 1,
          fullName: "Monday"
        },
        monda: {
          num: 1,
          fullName: "Monday"
        },
        monday: {
          num: 1,
          fullName: "Monday"
        },
        // Tuesday variations
        t: {
          num: 2,
          fullName: "Tuesday"
        },
        tu: {
          num: 2,
          fullName: "Tuesday"
        },
        tue: {
          num: 2,
          fullName: "Tuesday"
        },
        tues: {
          num: 2,
          fullName: "Tuesday"
        },
        tuesd: {
          num: 2,
          fullName: "Tuesday"
        },
        tuesda: {
          num: 2,
          fullName: "Tuesday"
        },
        tuesday: {
          num: 2,
          fullName: "Tuesday"
        },
        // Wednesday variations
        w: {
          num: 3,
          fullName: "Wednesday"
        },
        we: {
          num: 3,
          fullName: "Wednesday"
        },
        wed: {
          num: 3,
          fullName: "Wednesday"
        },
        wedn: {
          num: 3,
          fullName: "Wednesday"
        },
        wedne: {
          num: 3,
          fullName: "Wednesday"
        },
        wednes: {
          num: 3,
          fullName: "Wednesday"
        },
        wednesd: {
          num: 3,
          fullName: "Wednesday"
        },
        wednesda: {
          num: 3,
          fullName: "Wednesday"
        },
        wednesday: {
          num: 3,
          fullName: "Wednesday"
        },
        // Thursday variations
        th: {
          num: 4,
          fullName: "Thursday"
        },
        thu: {
          num: 4,
          fullName: "Thursday"
        },
        thur: {
          num: 4,
          fullName: "Thursday"
        },
        thurs: {
          num: 4,
          fullName: "Thursday"
        },
        thursd: {
          num: 4,
          fullName: "Thursday"
        },
        thursda: {
          num: 4,
          fullName: "Thursday"
        },
        thursday: {
          num: 4,
          fullName: "Thursday"
        },
        // Friday variations
        f: {
          num: 5,
          fullName: "Friday"
        },
        fr: {
          num: 5,
          fullName: "Friday"
        },
        fri: {
          num: 5,
          fullName: "Friday"
        },
        frid: {
          num: 5,
          fullName: "Friday"
        },
        frida: {
          num: 5,
          fullName: "Friday"
        },
        friday: {
          num: 5,
          fullName: "Friday"
        },
        // Saturday variations
        s: {
          num: 6,
          fullName: "Saturday"
        },
        sa: {
          num: 6,
          fullName: "Saturday"
        },
        sat: {
          num: 6,
          fullName: "Saturday"
        },
        satu: {
          num: 6,
          fullName: "Saturday"
        },
        satur: {
          num: 6,
          fullName: "Saturday"
        },
        saturd: {
          num: 6,
          fullName: "Saturday"
        },
        saturda: {
          num: 6,
          fullName: "Saturday"
        },
        saturday: {
          num: 6,
          fullName: "Saturday"
        }
      };
      const dayInfo = dayMap[dayInput];
      if (dayInfo) {
        const currentDayOfWeek = today_2.getDay();
        let daysUntilTarget = dayInfo.num - currentDayOfWeek;
        if (hasNext) {
          // For "next friday", always show the Friday of next week
          if (daysUntilTarget <= 0) {
            daysUntilTarget += 7;
          }
          daysUntilTarget += 7; // Force next week
        } else {
          // For just "friday", show this Friday if it's in the future, otherwise next Friday
          if (daysUntilTarget <= 0) {
            daysUntilTarget += 7;
          }
        }
        const targetDate_2 = addDays(today_2, daysUntilTarget);
        const label = hasNext ? `Next ${dayInfo.fullName}` : dayInfo.fullName;
        suggestions_2.push({
          id: `day-of-week-${dayInfo.num}${hasNext ? "-next" : ""}`,
          label: label,
          date: targetDate_2,
          isCustom: true
        });
      }
    }

    // Try to parse the input as a date first
    const parsedDate = parseNaturalLanguage(input_0);
    if (parsedDate) {
      // Check if this parsed date matches any existing suggestion
      const matchesExisting = suggestions_2.some(suggestion_0 => Math.abs(suggestion_0.date.getTime() - parsedDate.getTime()) < 24 * 60 * 60 * 1000 // Same day
      );

      // Check if this parsed date matches any predefined suggestion
      const matchesPredefined = predefined.some(suggestion_1 => Math.abs(suggestion_1.date.getTime() - parsedDate.getTime()) < 24 * 60 * 60 * 1000 // Same day
      );
      const isYearlessBoth = direction === "both" && yearlessMonthDayPattern.test(cleanInput_0);

      // If it doesn't match any existing or predefined suggestion, add it.
      // Yearless inputs in "both" mode always keep the primary so the
      // alt-year addition isn't the only visible suggestion when the
      // word-filter strips the matching predefined.
      if (!matchesExisting && (!matchesPredefined || isYearlessBoth)) {
        suggestions_2.push({
          id: "parsed-custom",
          label: format(parsedDate, "MMM d, yyyy"),
          date: parsedDate,
          isCustom: true
        });
      }

      // When direction allows both, also offer the alternate-year version
      // for bare month-day inputs (e.g. "june 15" → also show last year's
      // June 15). Restricted to month-day patterns so offset inputs like
      // "1 week" or "in 1 month" don't sprout a misleading second year.
      if (isYearlessBoth) {
        const todayForAlt = todayMarker();
        const altDate = new Date(parsedDate);
        altDate.setFullYear(parsedDate >= todayForAlt ? parsedDate.getFullYear() - 1 : parsedDate.getFullYear() + 1);
        const altPreservesDay = altDate.getMonth() === parsedDate.getMonth() && altDate.getDate() === parsedDate.getDate();
        const altAlreadyShown = suggestions_2.some(s => s.date.getFullYear() === altDate.getFullYear() && s.date.getMonth() === altDate.getMonth() && s.date.getDate() === altDate.getDate());
        if (altPreservesDay && !altAlreadyShown) {
          suggestions_2.push({
            id: "parsed-custom-alt-year",
            label: format(altDate, "MMM d, yyyy"),
            date: altDate,
            isCustom: true
          });
        }
      }
    }

    // Filter predefined suggestions that match the input
    const matchingPredefined = predefined.filter(suggestion_2 => {
      const label_0 = suggestion_2.label.toLowerCase();

      // Check for partial matches at word boundaries
      const words = cleanInput_0.split(/\s+/);
      return words.every(word => label_0.includes(word) ||
      // Special handling for common abbreviations and partial matches
      word === "tom" && label_0.includes("tomorrow") || word === "tod" && label_0.includes("today") || word === "yes" && label_0.includes("yesterday") || word === "yest" && label_0.includes("yesterday") || word === "we" && label_0.includes("week") || word === "mo" && label_0.includes("month") || word === "next" && label_0.includes("next") || word === "in" && label_0.includes("in") || word === "ago" && label_0.includes("ago") || word === "last" && label_0.includes("ago") ||
      // "last" maps to "ago" labels
      // Handle partial word matches for common terms
      "tomorrow".startsWith(word) && label_0.includes("tomorrow") || "today".startsWith(word) && label_0.includes("today") || "yesterday".startsWith(word) && label_0.includes("yesterday") || "week".startsWith(word) && label_0.includes("week") || "month".startsWith(word) && label_0.includes("month") || "months".startsWith(word) && label_0.includes("month") || "next".startsWith(word) && label_0.includes("next") || "ago".startsWith(word) && label_0.includes("ago"));
    });

    // Add matching predefined suggestions
    suggestions_2.push(...matchingPredefined);
    return suggestions_2;
  };

  // Update suggestions when input changes
  useEffect(() => {
    const newSuggestions = generateSuggestions(inputValue);
    setSuggestions(newSuggestions);
    setSelectedIndex(-1);
  }, [inputValue, customDefaultSuggestions, clock]);

  // Update input display when value changes (for SmartDateInput behavior)
  useEffect(() => {
    if (clearInputAfterSelection || open) return;
    if (controlledInputDisplay !== undefined) {
      setInputValue(controlledInputDisplay);
    } else if (value) {
      setInputValue(format(value, "MMM d, yyyy"));
    } else {
      setInputValue("");
    }
  }, [value, open, clearInputAfterSelection, controlledInputDisplay]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (!open) {
      justOpenedRef.current = true;
      setOpen(true);
    }
  };
  const handleInputFocus = () => {
    // Only set justOpenedRef if we're actually opening the popover
    if (!open) {
      justOpenedRef.current = true;
      setOpen(true);

      // If we have a selected value, show the empty input suggestions
      if (value) {
        const focusSuggestions = getEmptyInputSuggestions();
        setSuggestions(focusSuggestions);
        setSelectedIndex(-1);
      }
    }
  };
  const handleInputBlur = (e_0: React.FocusEvent<HTMLInputElement>) => {
    // Don't process blur if clicking on dropdown
    if (dropdownRef.current?.contains(e_0.relatedTarget as Node)) {
      return;
    }
    const trimmedInput = inputValue.trim();

    // Handle input parsing
    if (trimmedInput === "") {
      // Clear input but don't change the value
      if (!clearInputAfterSelection && (value || controlledInputDisplay !== undefined)) {
        setInputValue(controlledInputDisplay !== undefined ? controlledInputDisplay : format(value!, "MMM d, yyyy"));
      } else {
        setInputValue("");
      }
    } else {
      // Try to parse the current input value
      const parsedDate_0 = parseNaturalLanguage(trimmedInput);
      if (parsedDate_0) {
        onChange?.(parsedDate_0);
        if (clearInputAfterSelection) {
          setInputValue(""); // Clear input after selection (JumpToDate)
        } else {
          setInputValue(formatParsedDateDisplay ? formatParsedDateDisplay(parsedDate_0) : format(parsedDate_0, "MMM d, yyyy"));
        }
      } else {
        // Invalid input - restore previous value or clear
        if (!clearInputAfterSelection && (value || controlledInputDisplay !== undefined)) {
          setInputValue(controlledInputDisplay !== undefined ? controlledInputDisplay : format(value!, "MMM d, yyyy"));
        } else {
          setInputValue("");
        }
      }
    }

    // Close the popover when blurring
    setOpen(false);
  };
  const handleSuggestionSelect = (suggestion_3: DateSuggestion) => {
    if (onSuggestionSelect) {
      onSuggestionSelect(suggestion_3);
    } else {
      onChange?.(suggestion_3.date);
      if (clearInputAfterSelection) {
        setInputValue(""); // Clear input after selection (JumpToDate behavior)
      } else {
        setInputValue(format(suggestion_3.date, "MMM d, yyyy")); // Show formatted date (SmartDateInput behavior)
      }
      setOpen(false);
      inputRef.current?.blur();
    }
  };
  const handleKeyDown = (e_1: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e_1.key) {
      case "ArrowDown":
        e_1.preventDefault();
        if (!open) {
          setOpen(true);
          setSelectedIndex(0);
        } else {
          setSelectedIndex(prev_0 => prev_0 < suggestions.length - 1 ? prev_0 + 1 : prev_0);
        }
        break;
      case "ArrowUp":
        e_1.preventDefault();
        if (!open) {
          setOpen(true);
          setSelectedIndex(suggestions.length - 1);
        } else {
          setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        }
        break;
      case "Enter":
        e_1.preventDefault();
        if (open && selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        } else {
          // Try to parse current input
          const parsedDate_1 = parseNaturalLanguage(inputValue);
          if (parsedDate_1) {
            onChange?.(parsedDate_1);
            if (clearInputAfterSelection) {
              setInputValue("");
            } else {
              setInputValue(formatParsedDateDisplay ? formatParsedDateDisplay(parsedDate_1) : format(parsedDate_1, "MMM d, yyyy"));
            }
            setOpen(false);
          } else if (inputValue.trim() === "") {
            setInputValue("");
            setOpen(false);
          } else {
            // Invalid input - show suggestions or keep open
            if (!open) {
              setOpen(true);
            }
          }
        }
        break;
      case "Escape":
        e_1.preventDefault();
        if (open) {
          setOpen(false);
          setSelectedIndex(-1);
        } else {
          inputRef.current?.blur();
        }
        break;
      case "Tab":
        // Allow natural tab behavior, but close dropdown
        if (open) {
          setOpen(false);
          setSelectedIndex(-1);
        }
        break;
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && dropdownRef.current) {
      const selectedElement = dropdownRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: "nearest"
        });
      }
    }
  }, [selectedIndex]);
  return {
    open,
    setOpen,
    inputValue,
    setInputValue,
    selectedIndex,
    setSelectedIndex,
    suggestions,
    inputRef,
    dropdownRef,
    justOpenedRef,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleSuggestionSelect,
    handleKeyDown,
    parseNaturalLanguage,
    getSuggestionRightText
  };
}
export type { DateSuggestion, DateDirection };